import { WEB_BUILD_UPDATE_STORAGE_KEY } from '../../WebBuildUpdate';
import type { SipEventPort, SipStoragePort } from './contracts';

export interface WebBuildPhoneGateDeps {
  storage: SipStoragePort;
  events: SipEventPort;
  decodeTarget: (value: string | null) => string;
  onPendingChange: (change: { pending: boolean; previous: boolean }) => void;
}

export class WebBuildPhoneGate {
  private readonly storage: SipStoragePort;
  private readonly events: SipEventPort;
  private readonly decodeTarget: (value: string | null) => string;
  private readonly onPendingChange: WebBuildPhoneGateDeps['onPendingChange'];
  private readonly onStorage = (event: any) => {
    if (this.disposed || event?.key !== WEB_BUILD_UPDATE_STORAGE_KEY) return;
    this.applyTarget(this.decodeTarget(event?.newValue ?? null));
  };
  private buildId = '';
  private pending = false;
  private listenerBound = false;
  private disposed = false;

  constructor(deps: WebBuildPhoneGateDeps) {
    this.storage = deps.storage;
    this.events = deps.events;
    this.decodeTarget = deps.decodeTarget;
    this.onPendingChange = deps.onPendingChange;
  }

  setBuildId(buildId: string): void {
    if (this.disposed) return;
    this.buildId = String(buildId || '').trim();
    this.bindStorageListener();
    this.sync();
  }

  markStale(targetBuildId: string): boolean {
    if (this.disposed) return false;
    const target = String(targetBuildId || '').trim();
    if (!this.buildId || !target || target === this.buildId) return false;

    const previous = this.pending;
    this.pending = true;
    this.onPendingChange({ pending: true, previous });
    return true;
  }

  confirmCurrent(targetBuildId: string): boolean {
    if (this.disposed) return false;
    const target = String(targetBuildId || '').trim();
    if (!this.buildId || target !== this.buildId) return false;

    const previous = this.pending;
    this.pending = false;
    this.onPendingChange({ pending: false, previous });
    return true;
  }

  isPending(): boolean {
    return this.pending;
  }

  sync(): void {
    if (this.disposed || !this.buildId) return;

    try {
      this.applyTarget(this.decodeTarget(
        this.storage.getItem(WEB_BUILD_UPDATE_STORAGE_KEY),
      ));
    } catch {
      // В защищённом режиме браузера общее хранилище может быть недоступно.
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.listenerBound) {
      this.events.removeEventListener('storage', this.onStorage);
      this.listenerBound = false;
    }
  }

  private bindStorageListener(): void {
    if (this.listenerBound) return;
    this.events.addEventListener('storage', this.onStorage);
    this.listenerBound = true;
  }

  private applyTarget(targetBuildId: string): void {
    const target = String(targetBuildId || '').trim();
    if (!target) return;
    if (!this.confirmCurrent(target)) this.markStale(target);
  }
}
